import { Wishlist } from '@theme/wishlist';

const TOAST_DURATION = 2600;

/** @type {HTMLElement | null} */
let toastElement = null;
/** @type {number | undefined} */
let toastTimer;

/**
 * Shows the save confirmation. One toast is reused for the whole page.
 * @param {HTMLElement} source - The button that was pressed, for its copy and link.
 * @param {boolean} saved - Whether the product was saved or removed.
 */
function showToast(source, saved) {
  if (!toastElement) {
    toastElement = document.createElement('div');
    toastElement.className = 'wishlist-toast';
    toastElement.setAttribute('role', 'status');
    toastElement.setAttribute('aria-live', 'polite');
    document.body.append(toastElement);
  }

  const message = saved ? source.dataset.toastSaved : source.dataset.toastRemoved;
  const viewText = source.dataset.toastView;
  const viewUrl = source.dataset.wishlistUrl;

  toastElement.textContent = '';

  const label = document.createElement('span');
  label.className = 'wishlist-toast__text';
  label.textContent = message || '';
  toastElement.append(label);

  if (saved && viewUrl && viewText) {
    const link = document.createElement('a');
    link.className = 'wishlist-toast__link';
    link.href = viewUrl;
    link.textContent = viewText;
    toastElement.append(link);
  }

  toastElement.classList.add('wishlist-toast--visible');

  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toastElement?.classList.remove('wishlist-toast--visible'), TOAST_DURATION);
}

/**
 * A heart that saves the product it belongs to.
 *
 * Every button for the same product stays in sync through the `wishlist:change`
 * event, so the card, the product page and the wishlist page never disagree.
 */
class WishlistButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    this.#button = this.querySelector('button');
    this.#render();

    this.addEventListener('click', this.#handleClick);
    document.addEventListener(Wishlist.EVENT, this.#render);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(Wishlist.EVENT, this.#render);
  }

  get productId() {
    return this.dataset.productId ?? '';
  }

  /**
   * The heart sits inside a card whose whole surface is a link, so the click
   * must not bubble into it.
   * @param {Event} event
   */
  #handleClick = (event) => {
    if (!this.productId) return;

    event.preventDefault();
    event.stopPropagation();

    const saved = Wishlist.toggle(this.productId);
    showToast(this, saved);

    if (saved) {
      this.dataset.justSaved = 'true';
      setTimeout(() => delete this.dataset.justSaved, 550);
      this.#burst();
    }
  };

  /**
   * The save celebration: a ring of mini hearts bursts out of the button.
   * Rendered in a `position: fixed` layer so card containers can't clip it.
   */
  #burst() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const button = this.#button ?? this;
    const rect = button.getBoundingClientRect();

    const burst = document.createElement('span');
    burst.className = 'wishlist-burst';
    burst.style.left = `${rect.left + rect.width / 2}px`;
    burst.style.top = `${rect.top + rect.height / 2}px`;

    const HEART_PATH =
      'M12 20.75a.72.72 0 0 1-.42-.13c-.32-.21-7.79-5.27-10.24-9.76C-.12 8.18-.45 4.4 2.09 2a6.48 6.48 0 0 1 8.82 0L12 3l1.08-1a6.48 6.48 0 0 1 8.82 0c2.54 2.41 2.21 6.19.75 8.87-2.45 4.49-9.9 9.55-10.22 9.76a.72.72 0 0 1-.43.12';
    const COLORS = ['#c8102e', '#e6486b', '#f28ba8'];
    const COUNT = 7;

    for (let i = 0; i < COUNT; i += 1) {
      const particle = document.createElement('span');
      particle.className = 'wishlist-burst__particle';
      particle.innerHTML = `<svg viewBox="0 0 24 21" aria-hidden="true"><path d="${HEART_PATH}"/></svg>`;
      particle.style.color = COLORS[i % COLORS.length];
      particle.style.setProperty('--a', `${(360 / COUNT) * i + (Math.random() * 24 - 12)}deg`);
      particle.style.setProperty('--d', `${-(22 + Math.random() * 12)}px`);
      burst.append(particle);
    }

    document.body.append(burst);
    setTimeout(() => burst.remove(), 800);
  }

  #render = () => {
    const saved = Wishlist.has(this.productId);

    this.dataset.saved = String(saved);

    if (!this.#button) return;

    this.#button.setAttribute('aria-pressed', String(saved));

    const label = saved ? this.dataset.labelSaved : this.dataset.labelSave;
    if (label) this.#button.setAttribute('aria-label', label);

    const text = this.querySelector('.wishlist-button__label');
    if (text && label) text.textContent = label;
  };
}

/**
 * The count next to the heart in the header.
 */
class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.#render();
    document.addEventListener(Wishlist.EVENT, this.#render);
  }

  disconnectedCallback() {
    document.removeEventListener(Wishlist.EVENT, this.#render);
  }

  #render = () => {
    const count = Wishlist.count();
    const limit = parseInt(this.dataset.limit ?? '100', 10);
    const text = this.querySelector('[data-wishlist-count-text]');

    this.dataset.count = String(count);
    if (text) text.textContent = count === 0 ? '' : count < limit ? String(count) : `${limit - 1}+`;
  };
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
